import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatMiles } from "../../../../utils/miles";
import {
  editPagoProveedor,
  getDescuentosByPagoProveedor,
  addDescuentoProveedor as insertDescuentoProveedor,
  editDescuentoProveedor as updateDescuentoProveedor,
  deleteDescuentoProveedor,
} from "../../../../api/proveedorApi";
import { getPagoProveedoresByOrdenDePago } from "../../../../api/ordenesDePagoApi";
import { verifyOtpCode } from "../../../../api/OtpApi";
import { Modal, Button, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { FiEdit, FiCreditCard } from "react-icons/fi";
import "./sharedPagoStyles.css";
import * as XLSX from "xlsx";
/* =========================================================
 *  HELPERS
 * =======================================================*/
const isPaid = (estado) => String(estado || "").trim().toUpperCase() === "PAGADO";

/* Bloque OTP reutilizable dentro de cada modal */
function OtpInline({ onUnlocked }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      Swal.fire("Error", "Ingrese un código de 6 dígitos.", "error");
      return;
    }
    try {
      setBusy(true);
      await verifyOtpCode(trimmed);
      Swal.fire("Verificado", "Edición habilitada.", "success");
      onUnlocked();
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        "Código incorrecto o vencido.";
      Swal.fire("Error", msg, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8, padding: 10, border: "1px dashed #cbd5e1", borderRadius: 8, background: "#f8fafc" }}>
      <Form.Label style={{ fontWeight: 600, fontSize: 12 }}>Desbloquear con OTP</Form.Label>
      <div style={{ display: "flex", gap: 8 }}>
        <Form.Control
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="123456"
          style={{ maxWidth: 140 }}
        />
        <Button variant="secondary" onClick={verify} disabled={busy} title="Verificar y desbloquear">
          {busy ? "..." : "🔒"}
        </Button>
      </div>
      <small style={{ color: "#64748b" }}>Ingrese el código y presione el candado para editar.</small>
    </div>
  );
}

/* =========================================================
 *  COMPONENTE PRINCIPAL
 * =======================================================*/
function Proveedores({ idOrdenDePago, idordendepago, fechainicio, fechafin }) {
  const [proveedores, setProveedores] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Barra superior estilo Profesionales
  const [collapsed, setCollapsed] = useState(false);
  const [filtroMetodo, setFiltroMetodo] = useState("");    // método predominante
  const [filtroProveedor, setFiltroProveedor] = useState(""); // texto

  // Modales
  const [modalOpen, setModalOpen] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [descModalOpen, setDescModalOpen] = useState(false);

  // Selecciones
  const [selectedTrabajo, setSelectedTrabajo] = useState(null);
  const [selectedPago, setSelectedPago] = useState(null);
  const [selectedDescuento, setSelectedDescuento] = useState(null);

  // Caches
  const lastIdRef = useRef(null);
  const descuentosCacheRef = useRef(new Map()); // key: idpagoproveedor -> array descuentos

  /* ===========================
   *  CARGA DE PROVEEDORES + DESCUENTOS
   * ===========================*/
  useEffect(() => {
    const load = async () => {
      if (!idOrdenDePago) {
        setProveedores({});
        return;
      }

      // Evitar recarga si ya está cacheado
      if (lastIdRef.current === idOrdenDePago && Object.keys(proveedores).length)
        return;

      setLoading(true);
      const result = {};

      try {
        const data = await getPagoProveedoresByOrdenDePago(idOrdenDePago);

        // ✅ Si no hay datos, simplemente limpiar y salir sin error
        if (!Array.isArray(data) || data.length === 0) {
          console.info("⚠️ No hay proveedores cargados para esta orden de pago.");
          setProveedores({});
          lastIdRef.current = idOrdenDePago;
          setLoading(false);
          return;
        }

        // ✅ Procesar proveedores normalmente
        for (const prov of data) {
          const {
            proveedor,
            factura,
            monto,
            metododepago,
            estado,
            comprobante,
            idpagoproveedor,
            idProveedor,
            ruc,
            razonsocial,
            fechaorden,
          } = prov || {};

          const clave =
            (proveedor && proveedor.trim().toLowerCase()) ||
            "proveedor_desconocido";

          if (!result[clave]) {
            result[clave] = {
              nombre: proveedor || "Proveedor desconocido",
              trabajos: [],
              descuentos: [],
              metodoPredominante: null,
              porcentajeMetodoPredominante: 0,
              totalTrabajos: 0,
              totalDescuentos: 0,
              total: 0,
            };
          }

          // Trabajo principal
          const trabajo = {
            idProveedor: idProveedor || null,
            ruc: ruc || null,
            razonsocial: razonsocial || null,
            fechaorden: fechaorden || null,
            proveedor: proveedor || "Proveedor desconocido",
            factura: factura || "-",
            monto: Number(monto) || 0,
            metodo: metododepago || "-",
            estado: estado || "PENDIENTE",
            comprobante: comprobante || null,
            idpagoproveedor: idpagoproveedor || null,
          };

          result[clave].trabajos.push(trabajo);

          // Descuentos asociados al pago
          if (idpagoproveedor) {
            try {
              let descs = descuentosCacheRef.current.get(idpagoproveedor);
              if (!descs) {
                descs = await getDescuentosByPagoProveedor(idpagoproveedor);
                descuentosCacheRef.current.set(
                  idpagoproveedor,
                  Array.isArray(descs) ? descs : []
                );
              }

              if (Array.isArray(descs)) {
                result[clave].descuentos.push(
                  ...descs.map((d) => ({
                    iddescuentoproveedor: d.iddescuentoproveedor,
                    idpagoproveedor: d.idpagoproveedor,
                    idordendepago: d.idordendepago,
                    detalle: d.detalle,
                    monto: Number(d.monto || 0),
                    factura: trabajo.factura || "-",
                    metodo: trabajo.metodo || "-",
                    estadoPago: trabajo.estado || "PENDIENTE",
                  }))
                );
              }
            } catch (errDesc) {
              console.warn(
                `No se pudieron cargar descuentos del pago ${idpagoproveedor}:`,
                errDesc
              );
            }
          }
        }

        // Totales y método predominante
        Object.keys(result).forEach((cl) => {
          const p = result[cl];
          p.totalTrabajos = p.trabajos.reduce((acc, t) => acc + (t.monto || 0), 0);
          p.totalDescuentos = p.descuentos.reduce(
            (acc, d) => acc + (Number(d.monto) || 0),
            0
          );
          p.total = p.totalTrabajos + p.totalDescuentos;

          const methodCounts = {};
          let totalRegistrosMetodo = 0;
          p.trabajos.forEach((t) => {
            const clean = (t.metodo || "-").trim();
            if (clean && clean !== "-") {
              methodCounts[clean] = (methodCounts[clean] || 0) + 1;
              totalRegistrosMetodo++;
            }
          });

          if (totalRegistrosMetodo > 0) {
            let metodoPredominante = null;
            let countMax = 0;
            for (const [m, c] of Object.entries(methodCounts)) {
              if (c > countMax) {
                metodoPredominante = m;
                countMax = c;
              }
            }
            p.metodoPredominante = metodoPredominante;
            p.porcentajeMetodoPredominante =
              (countMax / totalRegistrosMetodo) * 100;
          }
        });

        setProveedores(result);
        lastIdRef.current = idOrdenDePago;
      } catch (e) {
        console.error("❌ Error cargando proveedores:", e);

        // 🚫 Evitar mostrar SweetAlert si el error es "no encontrado", 404 o 422
        const msg = e?.message?.toLowerCase?.() || "";
        const status = e?.status || e?.response?.status;

        if (
          msg.includes("no encontrado") ||
          msg.includes("no existen") ||
          msg.includes("sin datos") ||
          status === 404 ||
          status === 422
        ) {
          console.warn("⚠️ Sin proveedores disponibles para esta orden.");
          setProveedores({});
        } else {
          Swal.fire("Error", "No se pudo cargar proveedores.", "error");
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [idOrdenDePago]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalcular totales/métodos tras cambios
  const recalcProveedor = (prevObj, clave) => {
    const p = prevObj[clave];
    if (!p) return prevObj;
    p.totalTrabajos = p.trabajos.reduce((acc, t) => acc + (t.monto || 0), 0);
    p.totalDescuentos = p.descuentos.reduce((acc, d) => acc + (Number(d.monto) || 0), 0);
    p.total = p.totalTrabajos + p.totalDescuentos;

    const methodCounts = {};
    let totalRegistrosMetodo = 0;
    p.trabajos.forEach((t) => {
      const clean = (t.metodo || "-").trim();
      if (clean && clean !== "-") {
        methodCounts[clean] = (methodCounts[clean] || 0) + 1;
        totalRegistrosMetodo++;
      }
    });
    if (totalRegistrosMetodo > 0) {
      let metodoPredominante = null;
      let countMax = 0;
      for (const [m, c] of Object.entries(methodCounts)) {
        if (c > countMax) {
          metodoPredominante = m;
          countMax = c;
        }
      }
      p.metodoPredominante = metodoPredominante;
      p.porcentajeMetodoPredominante = (countMax / totalRegistrosMetodo) * 100;
    } else {
      p.metodoPredominante = null;
      p.porcentajeMetodoPredominante = 0;
    }
    return prevObj;
  };

  /* ===========================
   *  FILTROS Y RESUMEN (estilo Profesionales)
   * ===========================*/
  const metodosDisponibles = useMemo(() => {
    const set = new Set();
    Object.values(proveedores).forEach((p) => {
      if (p.metodoPredominante) set.add(p.metodoPredominante);
      p.trabajos.forEach((t) => {
        const m = (t.metodo || "").trim();
        if (m && m !== "-") set.add(m);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proveedores]);

  const proveedoresFiltrados = useMemo(() => {
    const entries = Object.entries(proveedores);
    return entries.filter(([_, p]) => {
      const nameMatch = p.nombre.toLowerCase().includes(filtroProveedor.toLowerCase());
      const methodMatch = !filtroMetodo || (p.metodoPredominante || "").toLowerCase() === filtroMetodo.toLowerCase();
      return nameMatch && methodMatch;
    });
  }, [proveedores, filtroProveedor, filtroMetodo]);

  const sortedProveedores = useMemo(
    () => proveedoresFiltrados.sort((a, b) => a[1].nombre.localeCompare(b[1].nombre)),
    [proveedoresFiltrados]
  );

  const globalSummary = useMemo(() => {
    const provCount = sortedProveedores.length;
    let trabajosRegs = 0;
    let descuentosRegs = 0;
    let totalGeneral = 0;
    sortedProveedores.forEach(([_, p]) => {
      trabajosRegs += p.trabajos.length;
      descuentosRegs += p.descuentos.length;
      totalGeneral += p.total || 0;
    });
    return { provCount, trabajosRegs, descuentosRegs, totalGeneral };
  }, [sortedProveedores]);

  /* ===========================
   *  ACCIONES / MODALES (abrir siempre; bloqueo dentro del modal)
   * ===========================*/
  const openEditModal = (trabajo, clave) => {
    setSelectedTrabajo({ ...trabajo, clave, nombreProveedor: proveedores[clave]?.nombre || "-" });
    setModalOpen(true);
  };

  const openPayModal = (trabajo, clave) => {
    setSelectedPago({ ...trabajo, clave, nombreProveedor: proveedores[clave]?.nombre || "-" });
    setPayModalOpen(true);
  };

  const openAddDescuento = (trabajo, clave) => {
    setSelectedDescuento({
      idpagoproveedor: trabajo.idpagoproveedor,
      idordendepago,
      clave,
      factura: trabajo.factura || "-",
      metodo: trabajo.metodo || "-",
      proveedor: proveedores[clave]?.nombre || "-",
      estadoPago: trabajo.estado || "PENDIENTE",
    });
    setDescModalOpen(true);
  };

  const openEditDescuento = (d, clave) => {
    const pago = proveedores[clave]?.trabajos?.find((t) => t.idpagoproveedor === d.idpagoproveedor);
    setSelectedDescuento({
      iddescuentoproveedor: d.iddescuentoproveedor,
      idpagoproveedor: d.idpagoproveedor,
      idordendepago: d.idordendepago || idordendepago,
      clave,
      detalle: d.detalle || "",
      monto: Number(d.monto || 0),
      factura: d.factura || "-",
      metodo: d.metodo || "-",
      proveedor: proveedores[clave]?.nombre || "-",
      estadoPago: pago?.estado || "PENDIENTE",
    });
    setDescModalOpen(true);
  };

  // actualizar factura
 const handleUpdateFactura = async (data) => {
  try {
    const payload = {
      idpagoproveedor: data.idpagoproveedor,
      idProveedor: data.idProveedor,
      proveedor: data.proveedor,
      ruc: data.ruc,
      razonsocial: data.razonsocial,
      factura: data.factura,
      monto: data.monto,
      fechaOrden: data.fechaorden || data.fechaOrden || null,
      estado: data.estado,
      metododepago: data.metododepago || data.metodo,
      comprobante: data.comprobante || null,
    };

    const response = await editPagoProveedor(payload);
    if (response) {
      setProveedores((prev) => {
        const up = { ...prev };
        const p = up[data.clave];
        if (p) {
          const t = p.trabajos.find(
            (x) => x.idpagoproveedor === data.idpagoproveedor
          );
          if (t) {
            t.factura = payload.factura || "-";
            t.metodo = payload.metododepago || "-";
            t.estado = payload.estado || t.estado;
            t.comprobante = payload.comprobante || t.comprobante;
            t.monto = payload.monto || t.monto; // ✅ actualiza el monto
          }
          recalcProveedor(up, data.clave); // ✅ recalcula totales
        }
        return { ...up };
      });
      setModalOpen(false);
      Swal.fire("Éxito", "Factura actualizada.", "success");
    }
  } catch (e) {
    console.error(e);
    Swal.fire("Error", "No se pudo actualizar la factura.", "error");
  }
};


  // registrar pago
  const handleSavePago = async (data) => {
    try {
      const payload = {
        idpagoproveedor: data.idpagoproveedor,
        idProveedor: data.idProveedor,
        proveedor: data.proveedor,
        ruc: data.ruc,
        razonsocial: data.razonsocial,
        factura: data.factura,
        monto: data.monto,
        fechaOrden: data.fechaorden || data.fechaOrden || null,
        estado: data.estado,
        metododepago: data.metododepago || data.metodo,
        comprobante: data.comprobante || null,
      };
      const response = await editPagoProveedor(payload);
      if (response) {
        setProveedores((prev) => {
          const up = { ...prev };
          const p = up[data.clave];
          if (p) {
            const t = p.trabajos.find((x) => x.idpagoproveedor === data.idpagoproveedor);
            if (t) {
              t.comprobante = payload.comprobante || "-";
              t.estado = payload.estado || "PENDIENTE";
            }
            recalcProveedor(up, data.clave);
          }
          return up;
        });
        setPayModalOpen(false);
        Swal.fire("Éxito", "Pago registrado correctamente.", "success");
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo registrar el pago.", "error");
    }
  };

  // prompt OTP para eliminar si está pagado
  const promptOtpIfPaid = async (estadoPago) => {
    if (!isPaid(estadoPago)) return true;
    const { value: code } = await Swal.fire({
      title: "OTP requerido",
      input: "text",
      inputLabel: "Ingrese el código de 6 dígitos",
      inputPlaceholder: "123456",
      inputAttributes: { maxlength: 6, inputmode: "numeric" },
      showCancelButton: true,
      confirmButtonText: "Validar",
      cancelButtonText: "Cancelar",
      preConfirm: async (value) => {
        const trimmed = String(value || "").trim();
        if (!/^\d{6}$/.test(trimmed)) {
          Swal.showValidationMessage("El código debe tener 6 dígitos.");
          return false;
        }
        try {
          await verifyOtpCode(trimmed);
          return true;
        } catch (err) {
          Swal.showValidationMessage(
            err?.response?.data?.message || "Código incorrecto o vencido."
          );
          return false;
        }
      },
    });
    return !!code;
  };

  // crear/editar descuento
  const handleSaveDescuento = async (obj) => {
    try {
      const isEdit = Boolean(obj.iddescuentoproveedor);
      const payload = {
        iddescuentoproveedor: obj.iddescuentoproveedor || null,
        idpagoproveedor: obj.idpagoproveedor,
        idordendepago: obj.idordendepago,
        detalle: obj.detalle,
        monto: Number(obj.monto), // mantener signo real
      };

      const saved = isEdit
        ? await updateDescuentoProveedor(payload)
        : await insertDescuentoProveedor(payload);

      if (saved) {
        setProveedores((prev) => {
          const up = { ...prev };
          const p = up[obj.clave];
          if (p) {
            if (isEdit) {
              const idx = p.descuentos.findIndex(
                (d) => d.iddescuentoproveedor === obj.iddescuentoproveedor
              );
              if (idx > -1) {
                p.descuentos[idx] = {
                  ...p.descuentos[idx],
                  detalle: payload.detalle,
                  monto: Number(payload.monto),
                };
              }
            } else {
              p.descuentos.push({
                iddescuentoproveedor: null, // tu API puede devolverlo—si vuelve, reemplazá luego
                idpagoproveedor: obj.idpagoproveedor,
                idordendepago: obj.idordendepago,
                detalle: payload.detalle,
                monto: Number(payload.monto),
                factura: obj.factura || "-",
                metodo: obj.metodo || "-",
                estadoPago: obj.estadoPago || "PENDIENTE",
              });
            }
            recalcProveedor(up, obj.clave);
          }
          return up;
        });

        setDescModalOpen(false);
        Swal.fire("Éxito", isEdit ? "Descuento actualizado." : "Descuento agregado.", "success");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo guardar el descuento.", "error");
    }
  };

  const handleDeleteDescuento = async (d, clave) => {
    const pago = proveedores[clave]?.trabajos?.find((t) => t.idpagoproveedor === d.idpagoproveedor);
    const estadoPago = pago?.estado || "PENDIENTE";
    const allowed = await promptOtpIfPaid(estadoPago);
    if (!allowed) return;

    Swal.fire({
      title: "¿Eliminar descuento?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        const done = await deleteDescuentoProveedor(d.iddescuentoproveedor);
        if (done) {
          setProveedores((prev) => {
            const up = { ...prev };
            const p = up[clave];
            if (p) {
              p.descuentos = p.descuentos.filter(
                (x) => x.iddescuentoproveedor !== d.iddescuentoproveedor
              );
              recalcProveedor(up, clave);
            }
            return up;
          });
          Swal.fire("Eliminado", "Descuento eliminado correctamente.", "success");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "No se pudo eliminar el descuento.", "error");
      }
    });
  };

  /* ===========================
   *  PDF (usa solo filtrados + agrega resumen general)
   * ===========================*/
  const handleDownloadPdf = () => {
    if (!sortedProveedores.length) return;
    const filtradosObj = Object.fromEntries(sortedProveedores);
    generateProveedoresPdf(
      filtradosObj,
      setGeneratingPdf,
      idordendepago,
      fechainicio,
      fechafin,
      {
        filtroMetodo: filtroMetodo || "Todos",
        filtroProveedor: filtroProveedor || "Todos",
      },
      globalSummary
    );
  };
  const handleDownloadExcel = () => {
    if (!sortedProveedores.length) {
      Swal.fire("Error", "No hay datos para exportar a Excel.", "error");
      return;
    }

    // Convertir lista filtrada a objeto simple
    const data = sortedProveedores.map(([_, p]) => ({
      Proveedor: p.nombre,
      "Método de Pago": p.metodoPredominante || "-",
      "Monto Total": p.total || 0,
    }));

    // Crear hoja y libro Excel
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Proveedores");

    // Opcional: ajustar anchos de columna
    ws["!cols"] = [
      { wch: 30 },
      { wch: 25 },
      { wch: 15 },
    ];

    // Guardar archivo
    const nombreArchivo = `Proveedores_orden_pago_${idordendepago}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);
  };

  const isEmpty = !loading && sortedProveedores.length === 0;

  /* ===========================
   *  RENDER
   * ===========================*/
  return (
    <div className="op-section theme-verde">
      {/* ======= Barra superior estilo Profesionales ======= */}
      <div className="proveedores-header">
        <div className="title-line">
          <span
            className="collapse-icon"
            role="button"
            aria-label={collapsed ? "Expandir" : "Colapsar"}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Mostrar detalle" : "Ocultar detalle"}
          >
            {collapsed ? "+" : "–"}
          </span>
          <h2 className="title-text">Proveedores</h2>

          {/* Resumen inline */}
          <div className="summary-inline">
            <span>
              Proveedores: <strong>{globalSummary.provCount}</strong>
            </span>
            <span>
              Trabajos: <strong>{globalSummary.trabajosRegs}</strong>
            </span>
            <span>
              Descuentos: <strong>{globalSummary.descuentosRegs}</strong>
            </span>
            <span>
              Total: <strong>{formatMiles(globalSummary.totalGeneral)}</strong>
            </span>
          </div>
        </div>

        {/* Filtros + PDF */}
        <div className="header-actions" style={{ display: "flex", gap: 8 }}>
          <div className="filtro-metodo-wrapper">
            <label htmlFor="metodoFilter" style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
              Método Predominante
            </label>
            <select id="metodoFilter" value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)}
              className="metodo-select"
            >
              <option value="">Todos</option>
              {metodosDisponibles.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-profesional-wrapper">
            <label
              htmlFor="proveedorFilter"
              style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}
            >
              Proveedor
            </label>
            <br />
            <input
              type="text"
              id="proveedorFilter"
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              placeholder="Buscar por nombre..."
              className="metodo-select"
              style={{ minWidth: 220, padding: "6px 8px" }}
            />
          </div>
          <button
            type="button"
            onClick={handleDownloadExcel}
            disabled={loading || isEmpty}
            className="excel-btn"
          >
            ⬇ Excel
          </button>

          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={loading || generatingPdf || isEmpty}
            className="pdf-btn"
          >
            {generatingPdf ? (
              <>
                <span className="spinner-mini" /> Generando...
              </>
            ) : (
              <>
                <span style={{ fontSize: 16 }}>⬇</span> PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* ======= Estado carga / vacío ======= */}
      {loading && (
        <div className="loading-inline">
          <div className="spinner" />
          <span>Cargando proveedores...</span>
        </div>
      )}
      {isEmpty && !loading && <p>No hay resultados con el filtro aplicado.</p>}

      {/* ======= Detalle colapsable con animación ======= */}
      {!isEmpty && (
        <div
          className="detalle-wrapper"
          style={{
            maxHeight: collapsed ? 0 : undefined,
            overflow: "hidden",
            transition: "max-height .4s ease",
          }}
        >
          {sortedProveedores.map(([clave, prov]) => (
            <div key={clave} className="proveedor-card">
              <h3>
                {prov.nombre}
                {prov.metodoPredominante && (
                  <small>
                    {" "}
                    Método predominante: {prov.metodoPredominante} (
                    {Number(prov.porcentajeMetodoPredominante || 0).toFixed(1)}%)
                  </small>
                )}
              </h3>

              <TablaTrabajos
                trabajos={prov.trabajos}
                clave={clave}
                onEdit={(t) => openEditModal(t, clave)}    // abrir siempre; bloqueo dentro
                onPay={(t) => openPayModal(t, clave)}      // abrir siempre; bloqueo dentro
                onAddDiscount={(t) => openAddDescuento(t, clave)} // abrir siempre; bloqueo dentro
              />

              {!!prov.descuentos.length && (
                <TablaDescuentos
                  descuentos={prov.descuentos}
                  clave={clave}
                  onEdit={(d) => openEditDescuento(d, clave)} // abrir siempre; bloqueo dentro
                  onDelete={(d) => handleDeleteDescuento(d, clave)}
                />
              )}

              <div className="totales-box">
                <span>Total Proveedor</span>
                <span style={{ color: "red" }}>{formatMiles(prov.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ======= Modales CRUD ======= */}
      {modalOpen && selectedTrabajo && (
        <EditFacturaModal
          trabajo={selectedTrabajo}
          onSave={handleUpdateFactura}
          onClose={() => setModalOpen(false)}
        />
      )}

      {payModalOpen && selectedPago && (
        <PagarFacturaModal
          pago={selectedPago}
          onSave={handleSavePago}
          onClose={() => setPayModalOpen(false)}
        />
      )}

      {descModalOpen && selectedDescuento && (
        <DescuentoModal
          data={selectedDescuento}
          onSave={handleSaveDescuento}
          onClose={() => setDescModalOpen(false)}
        />
      )}
    </div>
  );
}

/* =========================================================
 *  TABLAS (estilo Profesionales + opciones completas)
 * =======================================================*/
function TablaTrabajos({ trabajos, clave, onEdit, onPay, onAddDiscount }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="tabla-alineada">
        <thead>
          <tr>
            <th>Proveedor</th>
            <th>Factura</th>
            <th>Método</th>
            <th>Monto</th>
            <th>Estado</th>
            <th>Opc.</th>
          </tr>
        </thead>
        <tbody>
          {trabajos.map((t, idx) => {
            const locked = isPaid(t.estado);
            return (
              <tr key={`${clave}-${idx}`}>
                <td data-label="Proveedor">{t.proveedor || "-"}</td>
                <td data-label="Factura">{t.factura || "-"}</td>
                <td data-label="Método">{t.metodo || "-"}</td>
                <td data-label="Monto">{formatMiles(t.monto || 0)}</td>
                <td
                  data-label="Estado"
                  className={locked ? "estado-pagado" : "estado-pendiente"}
                  title={locked ? "PAGADO" : (t.estado || "PENDIENTE")}
                >
                  {t.estado || "PENDIENTE"}
                </td>
                <td data-label="Opc.">
                  <button
                    className="edit-btn"
                    onClick={() => onEdit(t)}
                    title={locked ? "Ver/Desbloquear con OTP" : "Editar factura"}
                  >
                    <FiEdit />
                  </button>
                  <button
                    className="edit-btn"
                    onClick={() => onPay(t)}
                    title={locked ? "Ver/Desbloquear con OTP" : "Registrar pago"}
                  >
                    <FiCreditCard />
                  </button>
                  <button
                    className="edit-btn"
                    onClick={() => onAddDiscount(t)}
                    title={locked ? "Ver/Desbloquear con OTP" : "Agregar descuento"}
                  >
                    ➕
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TablaDescuentos({ descuentos, clave, onEdit, onDelete }) {
  return (
    <div style={{ overflowX: "auto", marginTop: 8 }}>
      <table className="tabla-alineada">
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Factura</th>
            <th>Método</th>
            <th>Monto</th>
            <th>-</th>
            <th>Opc.</th>
          </tr>
        </thead>
        <tbody>
          {descuentos.map((d, idx) => (
            <tr key={`${clave}-desc-${idx}`}>
              <td data-label="Concepto">{d.detalle || "Descuento"}</td>
              <td data-label="Factura">{d.factura || "-"}</td>
              <td data-label="Método">{d.metodo || "-"}</td>
              <td
                data-label="Monto"
                style={{ color: Number(d.monto) < 0 ? "red" : undefined }}
              >
                {formatMiles(Number(d.monto) || 0)}
              </td>
              <td data-label="Estado">-</td>
              <td data-label="Opc.">
                <button
                  className="edit-btn"
                  onClick={() => onEdit(d)}
                  title="Editar descuento (si está PAGADO, se desbloquea con OTP)"
                >
                  <FiEdit />
                </button>
                <button
                  className="edit-btn"
                  onClick={() => onDelete(d)}
                  title="Eliminar descuento (si está PAGADO, requiere OTP)"
                >
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* =========================================================
 *  MODALES (con OTP integrado)
 * =======================================================*/
function EditFacturaModal({ trabajo, onSave, onClose }) {
  const [numeroFactura, setNumeroFactura] = useState(trabajo.factura || "");
  const [metodoDePago, setMetodoDePago] = useState(trabajo.metodo || "");
  const [monto, setMonto] = useState(formatMiles(trabajo.monto || 0));
  const [error, setError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  const locked = isPaid(trabajo.estado) && !unlocked;

  // 🔹 función para limpiar y convertir a número
  const parseMonto = (valor) => {
    const limpio = String(valor).replace(/\./g, "").replace(",", ".");
    const num = Number(limpio);
    return Number.isNaN(num) ? 0 : num;
  };

  // 🔹 formatea automáticamente con separadores al escribir
  const handleMontoChange = (e) => {
    const value = e.target.value.replace(/[^\d.,-]/g, "");
    const numeric = parseMonto(value);
    setMonto(formatMiles(numeric));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (locked) return;

    if (!numeroFactura.trim() || !metodoDePago.trim() || !monto) {
      setError("Por favor, complete todos los campos.");
      return;
    }

    const montoNum = parseMonto(monto);
    if (montoNum <= 0) {
      setError("El monto debe ser mayor a cero.");
      return;
    }

    setError("");
    const data = {
      ...trabajo,
      factura: numeroFactura,
      metododepago: metodoDePago,
      monto: montoNum,
      clave: trabajo.clave,
    };
    onSave(data);
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{locked ? "Ver Factura" : "Editar Factura"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Proveedor:</strong>{" "}
            {trabajo.nombreProveedor || trabajo.proveedor || "-"}
          </p>
          <p>
            <strong>Estado:</strong> {trabajo.estado || "PENDIENTE"}
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Número de Factura</Form.Label>
            <Form.Control
              type="text"
              value={numeroFactura}
              onChange={(e) => {
                const v = e.target.value;
                if (/^[0-9-]*$/.test(v)) setNumeroFactura(v);
              }}
              placeholder="Ej: 001-001-000153"
              disabled={locked}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Método de Pago</Form.Label>
            <Form.Control
              as="select"
              value={metodoDePago}
              onChange={(e) => setMetodoDePago(e.target.value)}
              disabled={locked}
            >
              <option value="">Seleccione un método</option>
              {["TRANSFERENCIA", "ACREDITACION_BANCARIA", "CHEQUE"].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Monto</Form.Label>
            <Form.Control
              type="text"
              value={monto}
              onChange={handleMontoChange}
              placeholder="Ej: 500.000"
              disabled={locked}
            />
          </Form.Group>

          {error && <p style={{ color: "red", fontSize: 12 }}>{error}</p>}

          {locked ? (
            <>
              <p style={{ color: "#9ca3af", fontSize: 12 }}>
                Este pago está pagado y no puede modificarse.
              </p>
              <OtpInline onUnlocked={() => setUnlocked(true)} />
            </>
          ) : (
            <p style={{ color: "#16a34a", fontSize: 12 }}>
              🔓 Edición habilitada.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={onClose}>
            Cerrar
          </Button>
          {!locked && (
            <Button type="submit" variant="success">
              Guardar
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}


function PagarFacturaModal({ pago, onSave, onClose }) {
  const [comprobante, setComprobante] = useState(pago.comprobante || "");
  const [estado, setEstado] = useState(pago.estado || "PENDIENTE");
  const [unlocked, setUnlocked] = useState(false);

  const locked = isPaid(pago.estado) && !unlocked;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (locked) return;
    const data = {
      ...pago,
      comprobante,
      estado,
      clave: pago.clave,
    };
    onSave(data);
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{locked ? "Ver Pago" : "Registrar Pago"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Proveedor:</strong> {pago.nombreProveedor || pago.proveedor || "-"}</p>
          <p><strong>Factura:</strong> {pago.factura || "-"}</p>
          <Form.Group className="mb-3">
            <Form.Label>Comprobante</Form.Label>
            <Form.Control
              type="text"
              value={comprobante}
              onChange={(e) => setComprobante(e.target.value)}
              placeholder="Ingrese número comprobante"
              disabled={locked}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Estado</Form.Label>
            <Form.Control
              as="select"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              disabled={locked}
            >
              <option value="PENDIENTE">Pendiente</option>
              <option value="PAGADO">Pagado</option>
            </Form.Control>
          </Form.Group>

          {locked ? (
            <>
              <p style={{ color: "#9ca3af", fontSize: 12 }}>
                Este pago está pagado y no puede modificarse.
              </p>
              <OtpInline onUnlocked={() => setUnlocked(true)} />
            </>
          ) : (
            <p style={{ color: "#16a34a", fontSize: 12 }}>🔓 Edición habilitada.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={onClose}>
            Cerrar
          </Button>
          {!locked && (
            <Button type="submit" variant="success">
              Guardar
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function DescuentoModal({ data, onClose, onSave }) {
  const isEdit = Boolean(data.iddescuentoproveedor);
  const [detalle, setDetalle] = useState(data.detalle || "");
  const [monto, setMonto] = useState(
    typeof data.monto === "number" ? String(data.monto) : ""
  );
  const [error, setError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  const locked = isPaid(data.estadoPago) && !unlocked;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (locked) return;
    if (!detalle.trim() || !String(monto).trim()) {
      setError("Complete los campos requeridos.");
      return;
    }
    setError("");
    const normalized = String(monto).replace(/\./g, "").replace(",", ".");
    const value = Number(normalized);
    if (Number.isNaN(value)) {
      setError("El monto debe ser un número.");
      return;
    }
    onSave({
      ...data,
      detalle,
      monto: value, // mantener signo
    });
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{locked ? "Ver Descuento" : isEdit ? "Editar Descuento" : "Nuevo Descuento"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Proveedor:</strong> {data.proveedor || "-"}</p>
          <p><strong>Factura:</strong> {data.factura || "-"}</p>
          <Form.Group className="mb-3">
            <Form.Label>Detalle</Form.Label>
            <Form.Control
              type="text"
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Motivo del descuento"
              disabled={locked}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Monto (admite negativo)</Form.Label>
            <Form.Control
              type="text"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="Ej: -50000"
              disabled={locked}
            />
          </Form.Group>
          {error && <p style={{ color: "red", fontSize: 12 }}>{error}</p>}

          {locked ? (
            <>
              <p style={{ color: "#9ca3af", fontSize: 12 }}>
                El pago está pagado; no puede modificarse este descuento.
              </p>
              <OtpInline onUnlocked={() => setUnlocked(true)} />
            </>
          ) : (
            <p style={{ color: "#16a34a", fontSize: 12 }}>🔓 Edición habilitada.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={onClose}>
            Cancelar
          </Button>
          {!locked && (
            <Button type="submit" variant="success">
              {isEdit ? "Actualizar" : "Guardar"}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* =========================================================
 *  PDF
 * =======================================================*/
function generateProveedoresPdf(
  proveedores,              // objeto ya filtrado y ordenado
  setGeneratingPdf,
  idordendepago,
  fechainicio,
  fechafin,
  filtros = { filtroMetodo: "Todos", filtroProveedor: "Todos" },
  resumen = { provCount: 0, trabajosRegs: 0, descuentosRegs: 0, totalGeneral: 0 }
) {
  setGeneratingPdf(true);
  try {
    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 28;
    const lineGap = 4;
    let cursorY = 36;

    // Encabezado
    doc
      .setFont("helvetica", "bold")
      .setFontSize(13)
      .text(
        `ORDEN DE PAGO PROVEEDORES #${idordendepago} - ${fechainicio || ""} - ${fechafin || ""}`,
        marginX,
        cursorY
      );
    cursorY += 14;

    doc
      .setFont("helvetica", "normal")
      .setFontSize(8)
      .text(`Generado: ${new Date().toLocaleString()}`, marginX, cursorY);
    cursorY += 10;

    // Filtros aplicados
    doc.setTextColor(30, 60, 120)
      .text(`Filtro Método Predominante: ${filtros.filtroMetodo}`, marginX, cursorY);
    cursorY += 10;
    doc.text(`Filtro Proveedor: ${filtros.filtroProveedor}`, marginX, cursorY);
    doc.setTextColor(0, 0, 0);
    cursorY += 12;

    // Resumen General
    const resumenBody = [
      ["Proveedores", String(resumen.provCount)],
      ["Trabajos", String(resumen.trabajosRegs)],
      ["Descuentos", String(resumen.descuentosRegs)],
      ["Total General", formatMiles(resumen.totalGeneral || 0)],
    ];
    doc.autoTable({
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 2, lineWidth: 0.1 },
      theme: "grid",
      headStyles: { fillColor: [230, 245, 235], textColor: 20 },
      head: [["Resumen", "Valor"]],
      body: resumenBody,
    });
    cursorY = doc.lastAutoTable.finalY + 12;

    const entries = Object.entries(proveedores).sort((a, b) =>
      a[1].nombre.localeCompare(b[1].nombre)
    );

    entries.forEach(([_, prov], idx) => {
      if (cursorY > pageHeight - 120) {
        doc.addPage();
        cursorY = 36;
      }

      // Título proveedor
      doc
        .setFont("helvetica", "bold")
        .setFontSize(10)
        .text(
          `${prov.nombre}  Total: ${formatMiles(prov.total)}${prov.metodoPredominante
            ? ` | Pred.: ${prov.metodoPredominante} (${Number(
              prov.porcentajeMetodoPredominante || 0
            ).toFixed(1)}%)`
            : ""
          }`,
          marginX,
          cursorY
        );
      cursorY += 12;

      // Tabla de trabajos
      if (prov.trabajos?.length) {
        doc.autoTable({
          startY: cursorY,
          margin: { left: marginX, right: marginX },
          styles: { fontSize: 7.5, cellPadding: 2, lineWidth: 0.1 },
          theme: "grid",
          headStyles: { fillColor: [240, 253, 244], textColor: 30 },
          head: [["Proveedor", "Factura", "Método", "Monto", "Estado", "Comprobante"]],
          body: prov.trabajos.map((t) => [
            t.proveedor || "-",
            t.factura || "-",
            t.metodo || "-",
            formatMiles(t.monto || 0),
            t.estado || "PENDIENTE",
            t.comprobante || "-",
          ]),
        });
        cursorY = doc.lastAutoTable.finalY + lineGap;
      }

      // Tabla de descuentos
      if (prov.descuentos?.length) {
        if (cursorY > pageHeight - 80) {
          doc.addPage();
          cursorY = 36;
        }
        doc.autoTable({
          startY: cursorY,
          margin: { left: marginX, right: marginX },
          styles: { fontSize: 7.5, cellPadding: 2, lineWidth: 0.1 },
          theme: "grid",
          headStyles: { fillColor: [245, 245, 245], textColor: 20 },
          head: [["Concepto", "Factura", "Método", "Monto"]],
          body: prov.descuentos.map((d) => [
            d.detalle || "Descuento",
            d.factura || "-",
            d.metodo || "-",
            formatMiles(Number(d.monto) || 0), // mantener signo
          ]),
        });
        cursorY = doc.lastAutoTable.finalY + lineGap;
      }

      // Caja total
      if (cursorY > pageHeight - 60) {
        doc.addPage();
        cursorY = 36;
      }
      doc.setDrawColor(22, 163, 74).setLineWidth(0.3).rect(marginX, cursorY, 240, 18);
      doc
        .setFont("helvetica", "bold")
        .setTextColor(220, 38, 38)
        .setFontSize(11)
        .text(`Total Proveedor: ${formatMiles(prov.total || 0)}`, marginX + 8, cursorY + 13);
      doc.setTextColor(0, 0, 0);
      cursorY += 26;

      if (idx < entries.length - 1) {
        doc.setDrawColor(200).setLineWidth(0.3).line(marginX, cursorY, pageWidth - marginX, cursorY);
        cursorY += 15;
      }
    });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7).setTextColor(120).text(`Página ${i} de ${totalPages}`, pageWidth - 80, pageHeight - 12);
    }

    const metodoSlug = (filtros.filtroMetodo || "Todos").replace(/\s+/g, "_");
    const provSlug = (filtros.filtroProveedor || "Todos").replace(/\s+/g, "_");
    doc.save(`Proveedores_orden_pago_${idordendepago}_${metodoSlug}_${provSlug}.pdf`);
  } catch (e) {
    console.error("Error generando PDF:", e);
    Swal.fire("Error", "No se pudo generar el PDF.", "error");
  } finally {
    setGeneratingPdf(false);
  }
}

export default Proveedores;
